import { getCatalog, getSingleMovie } from '../../../api/movies/index';
import { getSingleSeries, getSingleSeriesInfo } from '../../../api/series';
import { getUsersProfile } from '../../../api/users';
import { store } from '../../../utils/globalData';
import { checkImage } from '../../../utils/helpers';
import VODComponent from '../../Home/components/VODComponent';
import VODRow from '../../Home/components/VODRow';

const findSerie = (series, id) => {
  const serie = Object.values(series).find((serie) => serie.id === id);

  if (!serie) {
    console.error(`Series with id ${id} not found`);
    return null;
  }
  return serie;
};

const mapMovie = (movies, id, index, rowIndex) => {
  const movie = Object.values(movies).find((movie) => movie.id === id);

  if (!movie) {
    console.error(`Movie with id ${id} not found`);
    return null;
  }

  return {
    collision: true,
    type: VODComponent,
    isLocked: movie.tvod ?? false,
    src: checkImage(movie?.alternative_covers, movie?.['cover_art.expiring_url'] ?? ''),
    isProtected: movie?.locked ?? false,
    w: 204,
    h: 290,
    index, // Adding index
    rowIndex, // Adding rowIndex
    data: {
      kind: movie?.kind ?? 'Unknown',
      id: movie?.id ?? 'Unknown',
      title: movie?.name ?? 'No Title',
      description: movie?.description ?? 'No Description Available',
      cover: movie?.['screenshot.expiring_url'] ?? 'default_cover.png',
      category: movie?.tags ?? [],
      duration: movie?.duration ?? '0',
      rating: movie?.imdb?.rating === null ? '−' : movie?.imdb?.rating?.avg ?? '−',
      votes: movie?.imdb?.rating === null ? '−' : movie?.imdb?.rating?.num_votes ?? '−',
      contentRating: movie?.content_rating ?? 'Not Rated',
    },
  };
};

const mapSeries = (series, id, index, rowIndex) => {
  const serie = Object.values(series).find((serie) => serie.id === id);

  if (!serie) {
    console.error(`Series with id ${id} not found`);
    return null;
  }

  return {
    collision: true,
    type: VODComponent,
    src: checkImage(serie?.alternative_covers, serie?.['cover_art.expiring_url'] ?? ''),
    isProtected: false,
    w: 204,
    h: 290,
    index, // Adding index
    rowIndex, // Adding rowIndex
    data: {
      kind: serie?.kind ?? 'Unknown',
      id: serie?.id ?? 'Unknown',
      title: serie?.name ?? 'No Title',
      description: serie?.description ?? 'No Description Available',
      cover: serie?.['screenshot.expiring_url'] ?? 'default_cover.png',
      category: serie?.tags ?? [],
      duration: serie?.duration ?? '0',
      rating: serie?.imdb?.rating === null ? '−' : serie?.imdb?.rating?.avg ?? '−',
      votes: serie?.imdb?.rating === null ? '−' : serie?.imdb?.rating?.num_votes ?? '−',
      contentRating: serie?.content_rating ?? 'Not Rated',
    },
  };
};

const mapCollections = (collections, id, index, rowIndex) => {
  const collection = Object.values(collections).find((collection) => collection.id === id);
  return {
    collision: true,
    type: VODComponent,
    src: collection['cover_art.expiring_url'],
    isProtected: false,
    w: 290,
    h: 204,
    index, // Adding index
    rowIndex, // Adding rowIndex
    data: {
      kind: 'collection',
      id: collection.id,
      title: collection.name ?? '',
      cover: collection['screenshot.expiring_url'],
    },
  };
};

export default async function (page, { id }) {
  let data = store.getData();

  if (!data) {
    console.log('Nema u store, preuzimam podatke...');
    const catalogData = await getCatalog();
    data = catalogData.data;
    store.setData(data);
    store.setMyListFlag(true);
  } else {
    console.log('Podaci su već u store');
  }
  const rows = data.index['collections'][`${id}`].items;
  const categories = data.index;
  let featuredItem = data?.index['collections'][id]?.featured_items;
  let firstItem;
  const pageItems = rows
    .filter((row) => {
      const category = Object.values(categories[row.kind]).find(
        (category) => category.id === row.id
      );
      return category; // samo uključite redove koji imaju odgovarajuću kategoriju
    })
    .map((row, rowIndex) => {
      const category = Object.values(categories[row.kind]).find(
        (category) => category.id === row.id
      );
      const isCollectionRow = !category?.items?.some((item) => item.kind !== 'collection');
      const title = category?.name ?? '';
      const items = category?.items
        .map((item, index) => {
          switch (item.kind) {
            case 'movie':
              return mapMovie(categories.movies, item.id, index, rowIndex);
            case 'subseries':
              return mapSeries(categories.subseries, item.id, index, rowIndex);
            case 'collection':
              return mapCollections(categories.collections, item.id, index, rowIndex);
            default:
              return null;
          }
        })
        .filter((pageItem) => Boolean(pageItem));
      if (!isCollectionRow && !firstItem) {
        firstItem = category.items[0];
      }

      return {
        collision: true,
        scrollIndex: 2,
        type: VODRow,
        title,
        h: isCollectionRow ? 319 : 400,
        items: items.map((item, index) => ({ ...item, index: index })),
        style: { itemSpacing: 40 },
      };
    });

  let heroVOD = [];
  let heroVODObject;

  // Getting profile filter
  const { data: users } = await getUsersProfile();
  const filter = users?.user?.approvals || [];

  if (featuredItem && featuredItem.length > 0) {
    heroVOD = featuredItem[0];

    try {
      if (heroVOD?.kind === 'movie') {
        const data = await getSingleMovie(heroVOD?.id);
        if (data?.data?.movie) {
          let movie = data?.data?.movie;
          heroVODObject = {
            data: {
              isProtected: movie?.locked ?? true,
              title: movie?.title ?? '',
              description: movie?.description ?? '',
              cover: movie?.screenshot ?? 'default-cover.jpg',
              kind: heroVOD?.kind ?? '',
              id: movie?.id ?? '',
              category: movie?.tags ?? '',
              duration: movie?.duration ?? '',
              rating: movie?.imdb?.rating === null ? '−' : movie?.imdb?.rating?.avg ?? '',
              votes: movie?.imdb?.rating === null ? '−' : movie?.imdb?.rating?.num_votes ?? '',
              contentRating: movie?.content_rating ?? '',
              tvod: movie?.tvod ?? false,
              languages: movie?.languages ?? [],
            },
          };
        } else {
          heroVODObject = null;
        }
      } else if (heroVOD?.kind === 'subseries') {
        const serieDetails = findSerie(categories.subseries, heroVOD?.id);
        if (serieDetails) {
          const data = await getSingleSeries(heroVOD?.id);
          const seriesEntry = data?.data;
          let serieId;
          if (data?.data?.last_watched_movie_id) {
            serieId = data?.data?.last_watched_movie_id;
          } else {
            serieId = data.data?.data[0]?.movies[0]?.id;
          }
          const seriesInfoo = await getSingleSeriesInfo(serieId);
          const seriesInfo = seriesInfoo?.data?.movie;

          heroVODObject = {
            data: {
              isProtected: seriesInfo?.locked ?? true,
              title: seriesEntry?.name ?? '',
              description: seriesEntry?.description ?? '',
              cover: serieDetails['screenshot.expiring_url'] ?? 'default-cover.jpg',
              kind: heroVOD?.kind ?? '',
              id: seriesInfo?.id ?? '',
              category: seriesInfo?.tags ?? '',
              duration: serieDetails?.duration ?? '',
              rating: serieDetails?.imdb?.rating === null ? '−' : movie?.imdb?.rating?.avg ?? '',
              votes:
                serieDetails?.imdb?.rating === null ? '−' : movie?.imdb?.rating?.num_votes ?? '',
              contentRating: serieDetails?.content_rating ?? '',
              tvod: seriesInfo?.tvod ?? false,
              languages: seriesInfo?.languages ?? [],
              idEntrySerie: seriesEntry?.id,
            },
          };
        } else {
          heroVODObject = null;
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      heroVODObject = null;
    }
  }

  const pageItems2 = pageItems.filter((item) => item.title.trim() !== '');
  page.filter = filter;
  page._Column.items = pageItems2;
  page.heroVODObject = heroVODObject;
}
